import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Copy, Check,
  Sparkles, RefreshCw, LayoutDashboard, Send, Loader2, Lightbulb, Activity,
  BarChart3, Workflow, Store,
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pitch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    job_title: '',
    company: '',
    job_description: '',
    category: 'data_entry'
  });

  const [pitch, setPitch] = useState(null);

  // Pre-fill from job if passed via navigation
  useEffect(() => {
    if (location.state?.job) {
      const job = location.state.job;
      setFormData({
        job_title: job.title,
        company: job.company,
        job_description: job.description,
        category: job.category
      });
    }
  }, [location.state]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!formData.job_title || !formData.company) {
      toast.error('Please fill in job title and company');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/pitch/generate`, formData);
      setPitch(res.data);
      toast.success('Pitch generated!');
    } catch (err) {
      toast.error('Failed to generate pitch');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Demand Intel', path: '/demand' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: MessageSquare, label: 'Pitch Generator', path: '/pitch' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Activity, label: 'API Hub', path: '/hub' },
  ];

  const categories = [
    { value: 'data_entry', label: 'Data Entry' },
    { value: 'admin', label: 'Admin Assistant' },
    { value: 'customer_support', label: 'Customer Support' },
    { value: 'email_management', label: 'Email Management' },
    { value: 'scheduling', label: 'Scheduling' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'bookkeeping', label: 'Bookkeeping' },
    { value: 'lead_gen', label: 'Lead Generation' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 flex items-center justify-center">
                <Zap className="w-5 h-5 text-black" />
              </div>
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive 
                      ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' 
                      : 'text-muted-foreground hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { logout(); navigate('/'); }}
              className="w-full justify-start text-muted-foreground hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold">AI Pitch Generator</h1>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-400" />
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <div className="space-y-6">
              <div className="bg-[#0a0a0a] border border-white/5 p-6">
                <h2 className="font-bold mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-emerald-400" />
                  Job Details
                </h2>
                <form onSubmit={handleGenerate} className="space-y-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase mb-2 block">Job Title *</label>
                    <Input
                      value={formData.job_title}
                      onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                      placeholder="e.g., Data Entry Specialist"
                      className="bg-black/50 border-white/10 focus:border-emerald-500 rounded-sm h-12"
                      required
                      data-testid="pitch-job-title-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase mb-2 block">Company *</label>
                    <Input
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="e.g., TechCorp Solutions"
                      className="bg-black/50 border-white/10 focus:border-emerald-500 rounded-sm h-12"
                      required
                      data-testid="pitch-company-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase mb-2 block">Category</label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm h-12" data-testid="pitch-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-white/10">
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase mb-2 block">Job Description</label>
                    <Textarea
                      value={formData.job_description}
                      onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                      placeholder="Paste the job description here for better pitch personalization..."
                      className="bg-black/50 border-white/10 focus:border-emerald-500 rounded-sm min-h-[150px]"
                      data-testid="pitch-description-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm"
                    data-testid="generate-pitch-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating...</>
                    ) : (
                      <><Sparkles className="w-5 h-5 mr-2" /> Generate Pitch</>
                    )}
                  </Button>
                </form>
              </div>

              {/* Tips */}
              <div className="bg-violet-500/10 border border-violet-500/20 p-6">
                <h3 className="font-bold mb-3 flex items-center gap-2 text-violet-400">
                  <Lightbulb className="w-5 h-5" />
                  Pro Tips
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Include specific details from the job posting for personalization</li>
                  <li>• Mention the exact role title in your subject line</li>
                  <li>• Follow up within 3 days if no response</li>
                  <li>• Keep your pitch under 150 words for best response rates</li>
                </ul>
              </div>
            </div>

            {/* Output */}
            <div className="space-y-6">
              {pitch ? (
                <>
                  {/* Subject Line */}
                  <div className="bg-[#0a0a0a] border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-xs text-muted-foreground uppercase">Subject Line</label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(pitch.subject)}
                        className="h-8 text-muted-foreground hover:text-emerald-400"
                        data-testid="copy-subject-btn"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="font-medium text-lg">{pitch.subject}</p>
                  </div>

                  {/* Email Body */}
                  <div className="bg-[#0a0a0a] border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-xs text-muted-foreground uppercase">Email Body</label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(pitch.body)}
                        className="h-8 text-muted-foreground hover:text-emerald-400"
                        data-testid="copy-body-btn"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                    <div className="bg-black/40 p-4 border border-white/5 font-mono text-sm whitespace-pre-wrap">
                      {pitch.body}
                    </div>
                  </div>

                  {/* Follow-up */}
                  <div className="bg-amber-500/10 border border-amber-500/20 p-6">
                    <h3 className="font-bold mb-2 flex items-center gap-2 text-amber-400">
                      <RefreshCw className="w-5 h-5" />
                      Follow-up Strategy
                    </h3>
                    <p className="text-sm text-muted-foreground">{pitch.follow_up}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button
                      onClick={() => handleCopy(`Subject: ${pitch.subject}\n\n${pitch.body}`)}
                      className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12"
                      data-testid="copy-all-btn"
                    >
                      <Copy className="w-5 h-5 mr-2" /> Copy Full Email
                    </Button>
                    <Button
                      onClick={() => setPitch(null)}
                      variant="outline"
                      className="border-white/10 hover:border-emerald-500 rounded-sm h-12"
                      data-testid="clear-pitch-btn"
                    >
                      <RefreshCw className="w-5 h-5 mr-2" /> New Pitch
                    </Button>
                  </div>
                </>
              ) : (
                <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                  <div className="w-16 h-16 bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">Ready to Generate</h3>
                  <p className="text-muted-foreground mb-4">
                    Fill in the job details and click generate to create a personalized pitch.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Our AI analyzes the job requirements and crafts compelling outreach copy.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Pitch;